import React from "react";
import {
  Box,
  Typography,
  Button,
  Skeleton,
} from "@mui/material";
import clsx from "clsx";
import {
  KeyboardArrowRightRounded as KeyboardArrowRightRoundedIcon,
  KeyboardArrowLeftRounded as KeyboardArrowLeftRoundedIcon,
} from "@material-ui/icons";
import DoubleArrowLeft from "src/assets/img/doubleArrowLeft.png";
import DoubleArrowRight from "src/assets/img/doubleArrowRight.png";
import { makeStyles, withStyles } from "@material-ui/styles";
import { TextField } from "@material-ui/core";

const useStyle = makeStyles((theme) => ({
  header: {
    padding: "12px 16px",
  },
  container: {
    display: "grid",
    gridTemplateColumns: "7fr 0.7fr 7fr",
    columnGap: theme.spacing(2),
  },
  listContainer: {
    height: 300,
    maxHeight: "70vh",
    border: "1px solid #D0D5DD",
    overflowY: "auto",
  },
  summaryContainer: {
    border: "1px solid #D0D5DD",
  },
  branchRow: {
    "&:hover": {
      backgroundColor: "#F2F4F7",
    },
  },
  selected: {
    backgroundColor: "#E4E7EC",
    "&:hover": {
      backgroundColor: "#E4E7EC",
    },
  },
  smallButton: {
    height: 28,
    minWidth: 28,
    padding: 0,
  },
}));

const CustomTextField = withStyles({
  root: {
    root: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        paddingTop: 4,
        paddingBottom: 4,
        '& input': {
          padding: '6px 12px',
        },
      },
      '& .MuiInputLabel-outlined': {
        transform: 'translate(14px, 14px) scale(1)',
        transition: 'none',
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, 14px) scale(1)',
      },
      '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
      },
    },
  },
})(TextField);

function DualListSelector({
  titleLeft = "Available",
  titleRight = "Selected",
  isFetching,
  leftList,
  rightList,
  leftSelected,
  rightSelected,
  onLeftSelect,
  onRightSelect,
  onAllToRight,
  onToRight,
  onAllToLeft,
  onToLeft,
  getName,
  onLeftSearchChange,
  onRightSearchChange,
  leftSearch,
  rightSearch,
}) {
  const classes = useStyle();

  return (
    <Box className={classes.container}>
      {/* LEFT LIST */}
      <Box>
        <Box mb={1.5}>
          <Typography variant="body1">
            <b>{titleLeft}</b>
          </Typography>
        </Box>
        <CustomTextField
          placeholder="Search"
          variant="outlined"
          fullWidth
          onChange={onLeftSearchChange}
        />
        <Box className={classes.listContainer}>
          {isFetching ? (
            <Box p={1}>
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} variant="text" animation="wave" height={35} />
              ))}
            </Box>
          ) : (
            leftList.filter((e) => leftSearch.length === 0 ? true : getName(e).toLowerCase().includes(leftSearch.toLowerCase())).sort((a, b) => getName(a).localeCompare(getName(b))).map((item) => {
              const isSelected = leftSelected.some((i) => i === item);
              return (
                <Box
                  key={item}
                  py={1}
                  px={2}
                  onClick={() => onLeftSelect(item)}
                  className={clsx(classes.branchRow, {
                    [classes.selected]: !!isSelected,
                  })}
                >
                  <Typography variant="body1">{getName(item)}</Typography>
                </Box>
              );
            })
          )}
        </Box>
        <Box className={classes.summaryContainer} px={2} py={1}>
          <Typography variant="body2">
            <b>
              {leftList.length} available
            </b>
          </Typography>
        </Box>
      </Box>

      {/* ACTION BUTTONS */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Box mb={2}>
          <Button
            color="primary"
            variant="outlined"
            className={classes.smallButton}
            onClick={onAllToRight}
            disabled={!leftList.length}
          >
            <img
              src={DoubleArrowRight}
              alt="right"
              style={{ height: 10, opacity: !leftList.length ? 0.2 : 1 }}
            />
          </Button>
        </Box>
        <Box mb={2}>
          <Button
            color="primary"
            variant="outlined"
            className={classes.smallButton}
            onClick={onToRight}
            disabled={!leftList.length || !leftSelected.length}
          >
            <KeyboardArrowRightRoundedIcon />
          </Button>
        </Box>
        <Box mb={2}>
          <Button
            color="primary"
            variant="outlined"
            className={classes.smallButton}
            onClick={onToLeft}
            disabled={!rightList.length || !rightSelected.length}
          >
            <KeyboardArrowLeftRoundedIcon />
          </Button>
        </Box>
        <Box>
          <Button
            color="primary"
            variant="outlined"
            className={classes.smallButton}
            onClick={onAllToLeft}
            disabled={!rightList.length}
          >
            <img
              src={DoubleArrowLeft}
              alt="left"
              style={{ height: 10, opacity: !rightList.length ? 0.2 : 1 }}
            />
          </Button>
        </Box>
      </Box>

      {/* RIGHT LIST */}
      <Box>
        <Box mb={1.5}>
          <Typography variant="body1">
            <b>{titleRight}</b>
          </Typography>
        </Box>
        <CustomTextField
          placeholder="Search"
          variant="outlined"
          onChange={onRightSearchChange}
          fullWidth
        />
        <Box className={classes.listContainer}>
          {isFetching ? (
            <Box p={1}>
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} variant="text" animation="wave" height={35} />
              ))}
            </Box>
          ) : (
            rightList.filter((e) => rightSearch.length === 0 ? true : getName(e).toLowerCase().includes(rightSearch.toLowerCase())).sort((a, b) => getName(a).localeCompare(getName(b))).map((item) => {
              const isSelected = rightSelected.some((i) => i === item);
              return (
                <Box
                  key={item}
                  py={1}
                  px={2}
                  onClick={() => onRightSelect(item)}
                  className={clsx(classes.branchRow, {
                    [classes.selected]: !!isSelected,
                  })}
                >
                  <Typography variant="body1">{getName(item)}</Typography>
                </Box>
              );
            })
          )}
        </Box>
        <Box className={classes.summaryContainer} px={2} py={1}>
          <Typography variant="body2">
            <b>
              {rightList.length} selected
            </b>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default DualListSelector;
